import type {
  Announcement,
  AnnouncementApp,
  AnnouncementPlatform,
  AnnouncementTarget,
} from '../../../types/api/announcements.types';

/** One (app, platform) pair, which is what the backend calls a target. */
export type AudienceMember = {
  app: AnnouncementApp;
  platform: AnnouncementPlatform;
};

export type AudienceChip = AudienceMember & {
  label: string;
  /** Whether the member is part of the group's selection right now. */
  on: boolean;
};

/** What a target carries: since Oct 2026 an image OR a GIF, never both. */
export type MediaKind = 'image' | 'gif';

/** A saved image or GIF, as the group opened with it. */
export type StoredMedia = { kind: MediaKind; url: string };

/**
 * A named set of members that share one image (or GIF) and one minimum app
 * version.
 *
 * The backend has no notion of a group: it stores one row per target. Groups
 * exist so the admin uploads the phone-shaped image once for "everything
 * mobile" instead of once for iOS and again for Android, and
 * `flattenSelection` unrolls them back into one target per member on submit.
 */
export type AudienceGroup = {
  id: number;
  name: string;
  chips: AudienceChip[];
  /** Which of the two this group sends. Only the picked kind goes out. */
  mediaKind: MediaKind;
  /** A picked file, the stored URL being shown, or nothing. */
  media: File | Blob | string | null;
  /**
   * What the group opened with, so switching kinds and back shows it again,
   * and cancelling a replacement returns to it instead of to empty.
   */
  storedMedia: StoredMedia | null;
  minAppVersion: string;
};

/** A member paired with the group whose image and version it inherits. */
export type AudienceSelection = Array<
  AudienceMember & { group: AudienceGroup }
>;

/** Unique per member, so it works as both a map key and a React key. */
export const memberKey = ({ app, platform }: AudienceMember) =>
  `${app}_${platform}`;

/** How the API names a pair in its errors, so our wording matches its own. */
export const memberLabel = ({ app, platform }: AudienceMember) =>
  `${app} / ${platform}`;

/** The app's own name, unchanged - the API's words are the ones we use. */
const appLabel = (app: AnnouncementApp) => app;

export const audienceChip = (
  app: AnnouncementApp,
  platform: AnnouncementPlatform,
  on: boolean
): AudienceChip => ({
  app,
  platform,
  label: `${appLabel(app)} · ${platform}`,
  on,
});

/**
 * Every member the wizard offers.
 *
 * Tablet is Android only and admin is web only, because those are the only
 * builds that exist. A member listed here that nobody ships is a target an
 * announcement can be aimed at and never arrive.
 */
export const SCOPE_MEMBERS: AudienceMember[] = [
  { app: 'merchant', platform: 'ios' },
  { app: 'merchant', platform: 'android' },
  { app: 'merchant', platform: 'web' },
  { app: 'sales', platform: 'ios' },
  { app: 'sales', platform: 'android' },
  // { app: 'sales', platform: 'web' },
  { app: 'tablet', platform: 'android' },
  { app: 'admin', platform: 'web' },
];

/** "merchant + sales", from whichever apps a group actually targets. */
const groupNameFor = (members: AudienceMember[]) => {
  const apps = [...new Set(members.map((member) => appLabel(member.app)))];
  return apps.join(' + ') || 'Group';
};

/**
 * Every member as a chip, with the given ones turned on.
 *
 * A group always shows the whole scope, not only what it currently targets -
 * otherwise editing an announcement can only ever remove platforms from a
 * group, never add one, since the chip for the platform you want is not
 * drawn.
 *
 * Anything targeted but no longer in the scope (an app we stopped shipping)
 * is appended rather than dropped: submit replaces the entire target set, so
 * a chip that is not rendered is a target silently deleted on the next save.
 */
const chipsWith = (selected: AudienceMember[]): AudienceChip[] => {
  const on = new Set(selected.map(memberKey));
  const outsideScope = selected.filter(
    (member) =>
      !SCOPE_MEMBERS.some((scoped) => memberKey(scoped) === memberKey(member))
  );

  return [...SCOPE_MEMBERS, ...outsideScope].map((member) =>
    audienceChip(member.app, member.platform, on.has(memberKey(member)))
  );
};

/**
 * Rebuilds the wizard's groups from a saved announcement.
 *
 * Targets come back flat, one row per member, and the backend does not store
 * the grouping - so they are bucketed by minimum version, the one thing a
 * group carries that survives the round trip.
 *
 * Not by image: uploading one picture for a group stores a separate copy per
 * target, each under its own URL, so grouping on that gives one card per
 * target and an announcement written as a single group comes back as six.
 * The bucket shows the first target's image, and since an image is only sent
 * when a new file is picked, the others keep theirs untouched until then.
 */
export const groupsFromAnnouncement = (
  announcement: Announcement,
  nextId: () => number
): AudienceGroup[] => {
  const buckets = new Map<string, AnnouncementTarget[]>();

  announcement.targets.forEach((target) => {
    const key = target.minAppVersion ?? '';
    buckets.set(key, [...(buckets.get(key) ?? []), target]);
  });

  return [...buckets.values()].map((targets) => {
    const storedMedia = storedMediaOf(targets);

    return {
      id: nextId(),
      name: groupNameFor(targets),
      mediaKind: storedMedia?.kind ?? 'image',
      media: storedMedia?.url ?? null,
      storedMedia,
      minAppVersion: targets[0].minAppVersion ?? '',
      chips: chipsWith(targets),
    };
  });
};

/** The first saved image or GIF among these targets, whichever it is. */
const storedMediaOf = (targets: AnnouncementTarget[]): StoredMedia | null => {
  for (const { gifUrl, imageUrl } of targets) {
    if (gifUrl) return { kind: 'gif', url: gifUrl };
    if (imageUrl) return { kind: 'image', url: imageUrl };
  }
  return null;
};

/** A group with nothing but its members turned off, for the "add" button. */
export const emptyGroup = (id: number): AudienceGroup => ({
  id,
  name: 'New group',
  mediaKind: 'image',
  media: null,
  storedMedia: null,
  minAppVersion: '',
  chips: chipsWith([]),
});

/**
 * Flattens the groups into the targets the API stores.
 *
 * A member selected in two groups can only carry one image, and the last
 * group wins - which is what the person editing sees, since the group they
 * just turned it on in is below the one they forgot about.
 */
export const flattenSelection = (
  groups: AudienceGroup[]
): AudienceSelection => {
  const selected = new Map<string, AudienceMember & { group: AudienceGroup }>();

  groups.forEach((group) =>
    group.chips.forEach((chip) => {
      if (!chip.on) return;
      selected.set(memberKey(chip), {
        app: chip.app,
        platform: chip.platform,
        group,
      });
    })
  );

  return [...selected.values()];
};

/** A picked file, as opposed to the URL an edited group loaded with. */
export const hasFreshMedia = (group: AudienceGroup) =>
  !!group.media && typeof group.media !== 'string';

/**
 * The group switched to the other kind: what it shows now. The stored one
 * comes back when its kind is picked again; otherwise the picker is empty
 * until a file is chosen (and until then the saved one stays on the server,
 * since nothing is sent).
 */
export const withMediaKind = (
  group: AudienceGroup,
  mediaKind: MediaKind
): Partial<AudienceGroup> => ({
  mediaKind,
  media: group.storedMedia?.kind === mediaKind ? group.storedMedia.url : null,
});

/**
 * The members the saved announcement already holds an image or a GIF for.
 *
 * Every target must end up with exactly one, and the backend counts a target
 * as covered when a file arrives with the request *or* one is already stored
 * for that exact pair - so this is what separates a target that is fine
 * untouched from one the form still has to ask about.
 */
export const storedMediaKeys = (announcement: Announcement | null) =>
  new Set(
    (announcement?.targets ?? [])
      .filter((target) => target.imageUrl || target.gifUrl)
      .map(memberKey)
  );

/**
 * Selected members the server holds nothing for and the group has no fresh
 * file for, but whose card shows a saved image or GIF - typically a platform
 * switched on in a group that was loaded for editing.
 *
 * The API only takes files, and a new target without one is a 400, so on save
 * the shown file is downloaded and sent for exactly these members (see
 * `copyStoredMedia`). The members that already have it send nothing, which is
 * what keeps their own copy in place.
 */
export const membersNeedingCopy = (
  selection: AudienceSelection,
  stored: Set<string>
): AudienceSelection =>
  selection.filter(
    (member) =>
      !hasFreshMedia(member.group) &&
      typeof member.group.media === 'string' &&
      !stored.has(memberKey(member))
  );

/**
 * Selected members that would reach the API with neither an image nor a GIF,
 * which is a 400 for the whole request: nothing stored for the pair, no fresh
 * file, and nothing shown on the card that could be copied over.
 */
export const membersMissingMedia = (
  selection: AudienceSelection,
  stored: Set<string>
): AudienceSelection =>
  selection.filter(
    (member) => !member.group.media && !stored.has(memberKey(member))
  );

/** The group the preview stands for: the first selected one with media. */
export const previewGroup = (groups: AudienceGroup[]) =>
  groups.find((group) => group.media && group.chips.some((chip) => chip.on)) ??
  null;
